import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { CartLine, CartMap, Product } from './types';
import { clampToStock } from './availability';
import { maxOrderableQty, snapToMultiple } from './pricing';

// Client-held cart: productId → qty (always a multiple of moq, clamped to
// stock). Lives in React context for the marketplace screen flow.
interface MktCartValue {
  cart: CartMap;
  count: number;
  lines: (products: Product[]) => CartLine[];
  setQty: (product: Product, qty: number) => void;
  addMoq: (product: Product) => void;
  stepUp: (product: Product) => void;
  stepDown: (product: Product) => void;
  remove: (productId: string) => void;
  clear: () => void;
}

const MktCartContext = createContext<MktCartValue | null>(null);

export const MktCartProvider: React.FC = ({ children }) => {
  const [cart, setCart] = useState<CartMap>({});

  const setQty = useCallback((product: Product, qty: number) => {
    const moq = Math.max(product.moq, 1);
    // Shared PDP formula: snap to a MOQ multiple, and when stock caps the
    // value, cap to the largest orderable multiple — never a raw stock count.
    const { qty: snapped } = snapToMultiple(qty, moq);
    let next = clampToStock(product, snapped);
    if (next !== snapped && product.stock != null && product.stock >= moq) {
      next = maxOrderableQty(product.stock, moq);
    }
    setCart((prev) => ({ ...prev, [product.id]: next }));
  }, []);

  const addMoq = useCallback((product: Product) => {
    const moq = Math.max(product.moq, 1);
    setCart((prev) => {
      const stepped = (prev[product.id] || 0) + moq;
      let next = clampToStock(product, stepped);
      if (next !== stepped && product.stock != null && product.stock >= moq) {
        next = maxOrderableQty(product.stock, moq);
      }
      return { ...prev, [product.id]: next };
    });
  }, []);

  const stepUp = useCallback((product: Product) => {
    const moq = Math.max(product.moq, 1);
    setCart((prev) => {
      const stepped = (prev[product.id] || moq) + moq;
      let next = clampToStock(product, stepped);
      if (next !== stepped && product.stock != null && product.stock >= moq) {
        next = maxOrderableQty(product.stock, moq);
      }
      return { ...prev, [product.id]: next };
    });
  }, []);

  const stepDown = useCallback((product: Product) => {
    const moq = Math.max(product.moq, 1);
    setCart((prev) => ({
      ...prev,
      [product.id]: Math.max(moq, (prev[product.id] || moq) - moq),
    }));
  }, []);

  const remove = useCallback((productId: string) => {
    setCart((prev) => {
      const next = { ...prev };
      delete next[productId];
      return next;
    });
  }, []);

  const clear = useCallback(() => setCart({}), []);

  const lines = useCallback(
    (products: Product[]): CartLine[] =>
      Object.entries(cart)
        .filter(([, qty]) => qty > 0)
        .map(([id, qty]) => ({
          product: products.find((p) => p.id === id),
          qty,
        }))
        .filter((x): x is CartLine => !!x.product),
    [cart]
  );

  const count = useMemo(
    () => Object.values(cart).filter((q) => q > 0).length,
    [cart]
  );

  const value = useMemo(
    () => ({ cart, count, lines, setQty, addMoq, stepUp, stepDown, remove, clear }),
    [cart, count, lines, setQty, addMoq, stepUp, stepDown, remove, clear]
  );

  return <MktCartContext.Provider value={value}>{children}</MktCartContext.Provider>;
};

export const useMktCart = (): MktCartValue => {
  const ctx = useContext(MktCartContext);
  if (!ctx) throw new Error('useMktCart must be used within MktCartProvider');
  return ctx;
};
